export const OWNERSHIP_OTP_LENGTH = 6;

export type OwnershipOtpDigits = string[];

export type OwnershipOtpEdit = {
  digits: OwnershipOtpDigits;
  focusIndex: number;
};

function clampIndex(index: number): number {
  return Math.min(OWNERSHIP_OTP_LENGTH - 1, Math.max(0, index));
}

function normalizeDigits(digits: readonly string[]): OwnershipOtpDigits {
  return Array.from({ length: OWNERSHIP_OTP_LENGTH }, (_, index) => {
    const digit = digits[index] ?? "";
    return /^\d$/.test(digit) ? digit : "";
  });
}

export function emptyOwnershipOtpDigits(): OwnershipOtpDigits {
  return Array.from({ length: OWNERSHIP_OTP_LENGTH }, () => "");
}

/** Left-packed digit strings stay compact. A space keeps a cleared middle slot in place. */
export function ownershipOtpDigitsFromCode(code: string): OwnershipOtpDigits {
  if (code.includes(" ")) {
    return Array.from({ length: OWNERSHIP_OTP_LENGTH }, (_, index) => {
      const char = code[index] ?? "";
      return /^\d$/.test(char) ? char : "";
    });
  }
  const digits = code.replace(/\D/g, "").slice(0, OWNERSHIP_OTP_LENGTH);
  return Array.from({ length: OWNERSHIP_OTP_LENGTH }, (_, index) => digits[index] ?? "");
}

export function serializeOwnershipOtp(digits: readonly string[]): string {
  const normalized = normalizeDigits(digits);
  if (normalized.every((digit) => digit === "")) return "";
  return normalized
    .map((digit) => (digit === "" ? " " : digit))
    .join("")
    .replace(/\s+$/g, "");
}

/** One code string for the existing verify request. Incomplete slots do not submit. */
export function ownershipOtpSubmitCode(code: string): string | null {
  const digits = ownershipOtpDigitsFromCode(code);
  if (!digits.every((digit) => /^\d$/.test(digit))) return null;
  return digits.join("");
}

export function applyOwnershipOtpPaste(
  digits: readonly string[],
  index: number,
  rawText: string,
): OwnershipOtpEdit {
  const incoming = rawText.replace(/\D/g, "");
  const next = normalizeDigits(digits);
  if (incoming.length === 0) {
    return { digits: next, focusIndex: clampIndex(index) };
  }
  const start = incoming.length >= OWNERSHIP_OTP_LENGTH ? 0 : clampIndex(index);
  let write = start;
  for (const digit of incoming) {
    if (write >= OWNERSHIP_OTP_LENGTH) break;
    next[write] = digit;
    write += 1;
  }
  return { digits: next, focusIndex: Math.min(write, OWNERSHIP_OTP_LENGTH - 1) };
}

export function applyOwnershipOtpInput(
  digits: readonly string[],
  index: number,
  rawValue: string,
): OwnershipOtpEdit {
  const safeIndex = clampIndex(index);
  const next = normalizeDigits(digits);
  const current = next[safeIndex] ?? "";
  const incoming = rawValue.replace(/\D/g, "");

  if (incoming.length === 0) {
    next[safeIndex] = "";
    return { digits: next, focusIndex: safeIndex };
  }

  if (incoming.length === 1) {
    if (incoming === current) {
      return { digits: next, focusIndex: safeIndex };
    }
    next[safeIndex] = incoming;
    return {
      digits: next,
      focusIndex: safeIndex < OWNERSHIP_OTP_LENGTH - 1 ? safeIndex + 1 : safeIndex,
    };
  }

  if (incoming.length === 2 && current !== "" && incoming.includes(current)) {
    const typed = incoming[0] === current ? incoming[1] : incoming[0];
    if (typed === undefined || !/^\d$/.test(typed)) {
      return { digits: next, focusIndex: safeIndex };
    }
    next[safeIndex] = typed;
    return {
      digits: next,
      focusIndex: safeIndex < OWNERSHIP_OTP_LENGTH - 1 ? safeIndex + 1 : safeIndex,
    };
  }

  return applyOwnershipOtpPaste(next, safeIndex, incoming);
}

/** Filled slot clears in place. An empty slot clears the previous digit and moves there. */
export function applyOwnershipOtpBackspace(
  digits: readonly string[],
  index: number,
): OwnershipOtpEdit {
  const safeIndex = clampIndex(index);
  const next = normalizeDigits(digits);
  if (next[safeIndex] !== "") {
    next[safeIndex] = "";
    return { digits: next, focusIndex: safeIndex };
  }
  if (safeIndex === 0) {
    return { digits: next, focusIndex: 0 };
  }
  next[safeIndex - 1] = "";
  return { digits: next, focusIndex: safeIndex - 1 };
}

export function applyOwnershipOtpArrow(
  index: number,
  key: "ArrowLeft" | "ArrowRight",
): number {
  const safeIndex = clampIndex(index);
  if (key === "ArrowLeft") return Math.max(0, safeIndex - 1);
  return Math.min(OWNERSHIP_OTP_LENGTH - 1, safeIndex + 1);
}
